// @ts-check

/**
 * @file Renders the organizations index route.
 */

import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {useLocalSearchParams, useRouter} from "expo-router"
import {memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {Pressable, View} from "react-native"
import Link from "../../src/components/link"
import TextInput from "../../src/components/text-input"
import Button from "../../src/components/button"
import buildRoutePath from "../../src/shared/build-route-path"
import Card from "../../src/components/card"
import EmptyState from "../../src/components/empty-state"
import errorMessage from "../../src/shared/error-message"
import LoadingState from "../../src/components/loading-state"
import {Organization} from "../../src/frontend-models/organization"
import PageHeader from "../../src/components/page-header"
import Pagination from "../../src/components/pagination"
import useThemeColors, {useThemeName} from "../../src/shared/use-theme-colors"
import Text from "@kaspernj/api-maker/build/utils/text"
import ScreenTitle from "../../src/components/screen-title"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}
const PAGE_SIZE = 30

/** @typedef {{[key: string]: unknown}} OrganizationsIndexScreenProps */

/**
 * @typedef {object} OrganizationsIndexScreenState
 * @property {boolean} loading Whether organizations are loading.
 * @property {Organization[]} organizations Loaded organizations for the active page.
 * @property {number} page Current result page.
 * @property {string} searchQuery Current search filter text.
 * @property {{kind: "error", text: string} | null} status Error status shown above the list.
 * @property {number} totalCount Number of matching organizations.
 * @property {"all" | "customer" | "creditor"} typeFilter Selected organization type filter.
 */

/**
 * Renders organization filters, cards, and pagination.
 * @augments {ShapeComponent<OrganizationsIndexScreenProps, OrganizationsIndexScreenState>}
 */
class OrganizationsIndexScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  loadRequestId = 0
  searchDebounceTimer = /** @type {ReturnType<typeof setTimeout> | null} */ null
  /** @type {OrganizationsIndexScreenState} */
  state = {
    loading: true,
    organizations: /** @type {Organization[]} */ [],
    page: 1,
    searchQuery: "",
    status: /** @type {{kind: "error", text: string} | null} */ null,
    totalCount: 0,
    typeFilter: /** @type {"all" | "customer" | "creditor"} */ "customer"
  }

  /**
   * Initializes translations, route params, and loading effects.
   * @returns {void} Nothing.
   */
  setup() {
    useLocale()
    const params = useLocalSearchParams()
    const router = useRouter()

    this.params = params
    this.router = router
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    const page = this.s.page
    const searchQuery = this.s.searchQuery
    const typeFilter = this.s.typeFilter
    useEffect(() => {
      void this.loadOrganizations()
    }, [page, searchQuery, typeFilter])
    useEffect(() => {
      return () => {
        if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
      }
    }, [])
  }

  /**
   * Renders the organizations index route.
   * @returns {ReactNode} Organizations index content.
   */
  render() {
    const {loading, status} = this.s
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="organizationsIndexScreen"
      >
        <ScreenTitle>{_("Organizations")}</ScreenTitle>
        <View
          testID="organizationsIndexScreen/content"
        >
          <PageHeader
            actions={
              <Button
                href={buildRoutePath(this.tt.params, "/organizations/new")}
                iconName="plus"
                label={_("New organization")}
                testID="organizationsIndexScreen/newButton"
              />
            }
            subtitle={_("Manage organizations, customers and creditors")}
            testID="organizationsIndexScreen/header"
            title={_("Organizations")}
          />
          {this.renderSearchAndFilters()}
          {status &&
            <Text
              style={styles[`statusError-${this.tt.themeName}`] ||= {
                color: this.tt.colors.dangerText,
                fontWeight: 600,
                marginBottom: 16
              }}
              testID="organizationsIndexScreen/status"
            >
              {status.text}
            </Text>
          }
          {loading &&
            <LoadingState testID="organizationsIndexScreen/loadingText" />
          }
          {!loading && this.renderOrganizationsList()}
          {this.renderPagination()}
        </View>
      </View>
    )
  }

  /**
   * Renders pagination controls.
   * @returns {ReactNode} Pagination controls or null while loading.
   */
  renderPagination() {
    const {loading, page, totalCount} = this.s
    if (loading) return null
    return (
      <Pagination
        onPageChange={this.tt.onPageChange}
        page={page}
        pageSize={PAGE_SIZE}
        testID="organizationsIndexScreen/pagination"
        totalCount={totalCount}
      />
    )
  }

  /**
   * Renders search and type filter controls.
   * @returns {ReactNode} Search and filter controls.
   */
  renderSearchAndFilters() {
    const {typeFilter} = this.s
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    return (
      <View
        style={styles.searchAndFilters ||= {
          marginBottom: 16
        }}
        testID="organizationsIndexScreen/searchAndFilters"
      >
        <TextInput
          defaultValue={this.s.searchQuery}
          onChangeText={this.tt.onSearchChangeText}
          placeholder={_("Search organizations by name...")}
          style={styles.searchInput ||= {borderRadius: 10, marginBottom: 12}}
          testID="organizationsIndexScreen/searchInput"
        />
        <View
          style={styles.filterRow ||= {
            flexDirection: "row",
            gap: 8
          }}
          testID="organizationsIndexScreen/filterRow"
        >
          <Pressable
            onPress={() => this.onFilterPress("all")}
            style={styles[`filterButton-${themeName}-all-${typeFilter === "all"}`] ||= {
              alignItems: "center",
              backgroundColor: typeFilter === "all" ? colors.primary : colors.surface,
              borderColor: typeFilter === "all" ? colors.primary : colors.border,
              borderRadius: 999,
              borderWidth: 1,
              flexDirection: "row",
              paddingHorizontal: 14,
              paddingVertical: 8
            }}
            testID="organizationsIndexScreen/filterAll"
          >
            <View
              style={styles.filterIcon ||= {
                marginRight: 8,
                width: 14
              }}
              testID="organizationsIndexScreen/filterAll/icon"
            >
              <FontAwesome6 color={typeFilter === "all" ? colors.onPrimary : colors.textMuted} name="layer-group" size={13} />
            </View>
            <Text
              style={styles[`filterText-${themeName}-all-${typeFilter === "all"}`] ||= {
                color: typeFilter === "all" ? colors.onPrimary : colors.textMuted,
                fontSize: 13,
                fontWeight: 700
              }}
              testID="organizationsIndexScreen/filterAll/text"
            >
              {_("All")}
            </Text>
          </Pressable>
          <Pressable
            onPress={() => this.onFilterPress("customer")}
            style={styles[`filterButton-${themeName}-customer-${typeFilter === "customer"}`] ||= {
              alignItems: "center",
              backgroundColor: typeFilter === "customer" ? colors.primary : colors.surface,
              borderColor: typeFilter === "customer" ? colors.primary : colors.border,
              borderRadius: 999,
              borderWidth: 1,
              flexDirection: "row",
              paddingHorizontal: 14,
              paddingVertical: 8
            }}
            testID="organizationsIndexScreen/filterCustomer"
          >
            <View
              style={styles.filterIcon}
              testID="organizationsIndexScreen/filterCustomer/icon"
            >
              <FontAwesome6 color={typeFilter === "customer" ? colors.onPrimary : colors.textMuted} name="user-check" size={13} />
            </View>
            <Text
              style={styles[`filterText-${themeName}-customer-${typeFilter === "customer"}`] ||= {
                color: typeFilter === "customer" ? colors.onPrimary : colors.textMuted,
                fontSize: 13,
                fontWeight: 700
              }}
              testID="organizationsIndexScreen/filterCustomer/text"
            >
              {_("Customer")}
            </Text>
          </Pressable>
          <Pressable
            onPress={() => this.onFilterPress("creditor")}
            style={styles[`filterButton-${themeName}-creditor-${typeFilter === "creditor"}`] ||= {
              alignItems: "center",
              backgroundColor: typeFilter === "creditor" ? colors.primary : colors.surface,
              borderColor: typeFilter === "creditor" ? colors.primary : colors.border,
              borderRadius: 999,
              borderWidth: 1,
              flexDirection: "row",
              paddingHorizontal: 14,
              paddingVertical: 8
            }}
            testID="organizationsIndexScreen/filterCreditor"
          >
            <View
              style={styles.filterIcon}
              testID="organizationsIndexScreen/filterCreditor/icon"
            >
              <FontAwesome6 color={typeFilter === "creditor" ? colors.onPrimary : colors.textMuted} name="file-invoice-dollar" size={13} />
            </View>
            <Text
              style={styles[`filterText-${themeName}-creditor-${typeFilter === "creditor"}`] ||= {
                color: typeFilter === "creditor" ? colors.onPrimary : colors.textMuted,
                fontSize: 13,
                fontWeight: 700
              }}
              testID="organizationsIndexScreen/filterCreditor/text"
            >
              {_("Creditor")}
            </Text>
          </Pressable>
        </View>
      </View>
    )
  }

  /**
   * Renders organization result cards.
   * @returns {ReactNode} Organization cards or empty state.
   */
  renderOrganizationsList() {
    const {organizations} = this.s
    if (organizations.length === 0) {
      return (
        <EmptyState
          message={_("No organizations found")}
          testID="organizationsIndexScreen/emptyCard"
        />
      )
    }
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    return (
      <View
        style={styles.list ||= {
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 10
        }}
        testID="organizationsIndexScreen/list"
      >
        {organizations.map((organization) => {
          const orgId = organization.id()
          return (
            <Link
              asChild
              href={buildRoutePath(this.tt.params, `/organizations/${orgId}`)}
              key={orgId}
              style={styles.listItem ||= {
                flexBasis: "calc(50% - 5px)",
                flexGrow: 1,
                minWidth: 280
              }}
            >
              <Card
                style={styles.orgCardSpacing ||= {flex: 1}}
                testID={`organizationsIndexScreen/org-${orgId}`}
              >
                <View
                  style={styles.orgCardHeader ||= {
                    alignItems: "center",
                    flexDirection: "row",
                    gap: 12,
                    justifyContent: "space-between"
                  }}
                  testID={`organizationsIndexScreen/org-${orgId}/header`}
                >
                  <View
                    style={styles.orgCardNameRow ||= {
                      alignItems: "center",
                      flex: 1,
                      flexDirection: "row",
                      gap: 12
                    }}
                    testID={`organizationsIndexScreen/org-${orgId}/nameRow`}
                  >
                    <View
                      style={styles[`orgAvatar-${themeName}`] ||= {
                        alignItems: "center",
                        backgroundColor: colors.infoBackground,
                        borderRadius: 10,
                        height: 40,
                        justifyContent: "center",
                        width: 40
                      }}
                      testID={`organizationsIndexScreen/org-${orgId}/avatar`}
                    >
                      <Text
                        style={styles[`orgAvatarText-${themeName}`] ||= {
                          color: colors.link,
                          fontWeight: 700
                        }}
                        testID={`organizationsIndexScreen/org-${orgId}/avatar/text`}
                      >
                        {organizationInitials(organization.name())}
                      </Text>
                    </View>
                    <Text
                      style={styles[`orgName-${themeName}`] ||= {
                        flexShrink: 1,
                        fontSize: 16,
                        fontWeight: 700
                      }}
                      testID={`organizationsIndexScreen/org-${orgId}/name`}
                    >
                      {organization.name()}
                    </Text>
                  </View>
                  {this.renderTypeBadge(organization, orgId)}
                </View>
                <View
                  style={styles.orgMetricsRow ||= {
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 16,
                    marginTop: 12
                  }}
                  testID={`organizationsIndexScreen/org-${orgId}/metricsRow`}
                >
                  <Text
                    style={styles[`orgMetric-${themeName}`] ||= {
                      color: colors.textMuted,
                      fontSize: 12,
                      fontWeight: 600
                    }}
                    testID={`organizationsIndexScreen/org-${orgId}/projectsCount`}
                  >
                    {_("%{count} projects", {
                      count: String(organization.readCount("projectsCount"))
                    })}
                  </Text>
                  <Text
                    style={styles[`orgMetric-${themeName}`]}
                    testID={`organizationsIndexScreen/org-${orgId}/membersCount`}
                  >
                    {_("%{count} active members", {
                      count: String(organization.readCount("activeMembersCount"))
                    })}
                  </Text>
                  <Text
                    style={styles[`orgActivity-${themeName}`] ||= {
                      color: colors.textMuted,
                      fontSize: 12,
                      fontWeight: 500
                    }}
                    testID={`organizationsIndexScreen/org-${orgId}/activity`}
                  >
                    {this.lastActivityLabel(organization)}
                  </Text>
                </View>
              </Card>
            </Link>
          )
        })}
      </View>
    )
  }

  /**
   * Builds the organization activity label.
   * @param {Organization} organization Organization to inspect.
   * @returns {string} Localized activity text.
   */
  lastActivityLabel(organization) {
    const updatedAt = organization.updatedAt()
    if (!updatedAt) return _("No activity yet")
    return _("Last activity: %{date}", {
      date: updatedAt.toLocaleDateString()
    })
  }

  /**
   * Renders the organization type badge.
   * @param {Organization} organization Organization to inspect.
   * @param {string} orgId Organization identifier used for test ids.
   * @returns {ReactNode} Type badge or null.
   */
  renderTypeBadge(organization, orgId) {
    const isCustomer = organization.customer()
    const isCreditor = organization.creditor()
    if (!isCustomer && !isCreditor) return null
    const label = isCustomer ? _("Customer") : _("Creditor")
    const badgeKey = isCustomer ? "customer" : "creditor"
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    return (
      <View
        style={styles[`typeBadge-${themeName}-${badgeKey}`] ||= {
          backgroundColor: isCustomer ? colors.infoBackground : colors.surfaceMuted,
          borderRadius: 999,
          paddingHorizontal: 10,
          paddingVertical: 4
        }}
        testID={`organizationsIndexScreen/org-${orgId}/badge`}
      >
        <Text
          style={styles[`typeBadgeText-${themeName}-${badgeKey}`] ||= {
            color: isCustomer ? colors.infoText : colors.textMuted,
            fontSize: 12,
            fontWeight: 700
          }}
          testID={`organizationsIndexScreen/org-${orgId}/badge/text`}
        >
          {label}
        </Text>
      </View>
    )
  }

  /**
   * Debounces search text updates.
   * @param {string} value Search input value.
   * @returns {void} Nothing.
   */
  onSearchChangeText = (value) => {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
    this.searchDebounceTimer = setTimeout(() => {
      if (value !== this.s.searchQuery) {
        this.s.page = 1
        this.s.searchQuery = value
      }
    }, 300)
  }

  /**
   * Applies the selected type filter.
   * @param {"all" | "customer" | "creditor"} filterKey Filter key to apply.
   * @returns {void} Nothing.
   */
  onFilterPress = (filterKey) => {
    if (filterKey === this.s.typeFilter) return
    this.s.page = 1
    this.s.typeFilter = filterKey
  }

  /**
   * Applies the selected result page.
   * @param {number} nextPage Page to load.
   * @returns {void} Nothing.
   */
  onPageChange = (nextPage) => {
    if (nextPage === this.s.page) return
    this.s.page = nextPage
  }

  /**
   * Loads organizations for the active filters and page.
   * @returns {Promise<void>} Resolves after organizations are loaded.
   * @rejects {Error} When loading organizations fails.
   */
  loadOrganizations = async () => {
    const {page, searchQuery, typeFilter} = this.s
    const requestId = ++this.loadRequestId
    this.s.loading = true
    this.s.status = null
    try {
      /** @type {Record<string, unknown>} */
      const ransackParams = {
        s: "name asc, id asc"
      }
      if (searchQuery) ransackParams.nameCont = searchQuery
      if (typeFilter === "customer") ransackParams.customerEq = true
      if (typeFilter === "creditor") ransackParams.creditorEq = true
      const baseQuery = Organization.ransack(ransackParams)
      const [organizations, totalCount] = await Promise.all([
        baseQuery
          .clone()
          .withCount({
            projectsCount: {relationship: "projects"},
            activeMembersCount: {
              relationship: "primaryUsers",
              where: {active: true}
            }
          })
          .page(page)
          .perPage(PAGE_SIZE)
          .toArray(),
        baseQuery.clone().count()
      ])
      if (this.loadRequestId !== requestId) return
      this.s.loading = false
      this.s.organizations = organizations
      this.s.totalCount = totalCount
    } catch (error) {
      if (this.loadRequestId !== requestId) return
      this.s.loading = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      throw error
    }
  }
}

/** @type {NamedExoticComponent<OrganizationsIndexScreenProps>} */
const OrganizationsIndexScreenComponent = memo(shapeComponent(OrganizationsIndexScreen))

export default OrganizationsIndexScreenComponent

/**
 * Builds initials for an organization avatar.
 * @param {string | null} name Name to abbreviate.
 * @returns {string} Initials or fallback marker.
 */
function organizationInitials(name) {
  if (!name) return "?"
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return "?"
  if (words.length === 1) return words[0].charAt(0).toUpperCase()
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase()
}
